import React, { useMemo, useRef, useState } from 'react'
import { Box, Button, Divider, Menu, MenuItem, Typography } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CheckIcon from '@mui/icons-material/Check'
import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSelectedMarket } from '../../providers/Market'
import { fetchJson } from '../../utils/fetchJson'

type MarketRow = { id: number; name: string }

const marketsQuery = queryOptions<MarketRow[]>({
  queryKey: ['markets'],
  queryFn: () => fetchJson<MarketRow[]>('/api/markets'),
  staleTime: Number.POSITIVE_INFINITY,
  retry: 2,
})

export const MarketPicker: React.FC = () => {
  const { market, setMarket, isDetected, isLoading } = useSelectedMarket()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const open = anchorEl != null
  const queryClient = useQueryClient()
  const { data, isLoading: marketsLoading } = useQuery({
    ...marketsQuery,
    enabled: open,
  })
  const prefetchMarkets = () => queryClient.prefetchQuery(marketsQuery)
  const scrolledRef = useRef(false)

  const markets = useMemo(
    () => (data ?? []).map((m) => ({ marketId: m.id, marketName: m.name })),
    [data],
  )

  const close = () => setAnchorEl(null)

  return (
    <>
      <Button
        onClick={(e) => setAnchorEl(e.currentTarget)}
        onMouseEnter={prefetchMarkets}
        onFocus={prefetchMarkets}
        onTouchStart={prefetchMarkets}
        aria-expanded={open}
        variant="outlined"
        color="inherit"
        endIcon={<ExpandMoreIcon sx={{ flexShrink: 0 }} />}
        size="small"
        sx={{
          borderRadius: 999,
          textTransform: 'uppercase',
          fontFamily: (theme) => theme.app.robotoMonoFontFamily,
          fontSize: '0.6875rem',
          fontWeight: 700,
          letterSpacing: '0.04em',
          bgcolor: 'common.black',
          borderColor: 'rgba(255, 255, 255, 0.24)',
          color: 'common.white',
          maxWidth: 200,
          flexShrink: 0,
          "&:hover, &[aria-expanded='true']": {
            bgcolor: 'common.black',
            borderColor: 'rgba(255, 255, 255, 0.36)',
            color: 'common.white',
          },
          '&&': { px: 2.25, py: 0.5 },
        }}
      >
        {market && (
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              flexShrink: 0,
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: isDetected ? 'success.main' : 'primary.main',
              mr: 1,
            }}
          />
        )}
        <Box
          component="span"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {market
            ? market.marketName
            : isLoading
              ? 'Detecting…'
              : 'Select market'}
        </Box>
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              mt: '10px',
              width: 280,
              maxHeight: 420,
              border: 'none',
              borderRadius: '12px',
              boxShadow: 'none',
              '&&': { p: 0 },
            },
          },
          list: { sx: { p: 2 } },
        }}
      >
        <Typography
          sx={{
            pb: 0.5,
            fontFamily: (theme) => theme.app.robotoMonoFontFamily,
            fontSize: '0.6875rem',
            fontWeight: 500,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'neutral.mediumGray',
          }}
        >
          Select your market
        </Typography>
        <MenuItem
          onClick={() => {
            setMarket(null)
            close()
          }}
          selected={isDetected}
          sx={{
            justifyContent: 'space-between',
            gap: 1,
            py: 1.25,
            backgroundClip: 'border-box',
            fontWeight: isDetected ? 700 : 400,
            '&&:hover, &&.Mui-selected:hover': {
              bgcolor: 'action.hover',
              backgroundClip: 'border-box',
            },
            '&&.Mui-selected': { bgcolor: 'transparent' },
          }}
        >
          <Box
            component="span"
            sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}
          >
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                flexShrink: 0,
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: isDetected ? 'success.main' : 'transparent',
                border: isDetected ? 'none' : '1px solid',
                borderColor: 'neutral.lightGray',
              }}
            />
            Auto-detect my location
          </Box>
          {isDetected && <CheckIcon sx={{ fontSize: 18 }} />}
        </MenuItem>
        <Divider />
        {marketsLoading && <MenuItem disabled>Loading…</MenuItem>}
        {!marketsLoading && markets.length === 0 && (
          <MenuItem disabled>No markets found</MenuItem>
        )}
        {!marketsLoading &&
          markets.map((m) => {
            const selected = !isDetected && m.marketId === market?.marketId
            return (
              <MenuItem
                key={m.marketId}
                selected={selected}
                ref={
                  selected
                    ? (node) => {
                        if (node && !scrolledRef.current) {
                          node.scrollIntoView({ block: 'center' })
                          scrolledRef.current = true
                        }
                      }
                    : undefined
                }
                onClick={() => {
                  setMarket(m)
                  close()
                }}
                sx={{
                  justifyContent: 'space-between',
                  gap: 1,
                  py: 1.25,
                  minWidth: 0,
                  backgroundClip: 'border-box',
                  fontWeight: selected ? 700 : 400,
                  '&&:hover, &&.Mui-selected:hover': {
                    bgcolor: 'action.hover',
                    backgroundClip: 'border-box',
                  },
                  '&&.Mui-selected': { bgcolor: 'transparent' },
                }}
              >
                <Box
                  component="span"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                  }}
                >
                  {m.marketName}
                </Box>
                {selected && <CheckIcon sx={{ fontSize: 18, flexShrink: 0 }} />}
              </MenuItem>
            )
          })}
      </Menu>
    </>
  )
}
